// Industrial Pharmaceutical Manufacturing Theme
// Following UI/UX industry standards for both Light and Dark modes
// Based on Material Design 3, IBM Carbon Design System, and pharmaceutical industry standards

import { theme } from 'antd';

/**
 * INDUSTRIAL COLOR SYSTEM - UI/UX Standards Compliant
 *
 * Primary: Industrial Teal (#204448) - Professional, trustworthy, pharmaceutical-grade
 * Success: Industrial Green - Safe operations, approvals
 * Warning: Industrial Amber - Caution, attention needed
 * Error: Industrial Red - Critical alerts, errors
 * Info: Industrial Steel Blue - Information, guidance
 *
 * NO GRADIENTS - Solid colors only for industrial clarity
 * WCAG AA Compliant - Minimum contrast ratio 4.5:1 for text
 */

// Core Industrial Color Palette
const INDUSTRIAL_PALETTE = {
  // Primary - Industrial Teal/Cyan (Professional pharmaceutical color)
  teal: {
    50: '#e6eef0',   // Lightest teal
    100: '#c2d7db',
    200: '#9abfc5',
    300: '#72a7af',
    400: '#4d929b',
    500: '#2a6872',  // Base teal
    600: '#204448',  // DEFAULT - Primary brand color
    700: '#1a3639',  // Darker teal
    800: '#14292b',  // Very dark teal
    900: '#0e1c1d',  // Darkest teal
  },

  // Success - Industrial Green (Safe operations, approvals)
  green: {
    50: '#e8f5ec',
    100: '#c5e5cf',
    200: '#9fd4b0',
    300: '#78c390',
    400: '#5bb678',
    500: '#3ea960',  // Base green
    600: '#2d7a3e',  // DEFAULT - Primary success
    700: '#1f5c2c',  // Darker green
    800: '#144520',  // Very dark green
    900: '#0a2e14',  // Darkest green
  },

  // Warning - Industrial Amber (Caution, attention)
  amber: {
    50: '#fef7ec',
    100: '#fceace',
    200: '#fadcae',
    300: '#f8ce8e',
    400: '#f6c475',
    500: '#f4b95d',  // Base amber
    600: '#d97706',  // DEFAULT - Primary warning
    700: '#b85c05',  // Darker amber
    800: '#964704',  // Very dark amber
    900: '#6b3203',  // Darkest amber
  },

  // Error - Industrial Red (Critical alerts)
  red: {
    50: '#fdecea',
    100: '#facfc9',
    200: '#f7afa6',
    300: '#f48f83',
    400: '#f17768',
    500: '#ef5f4c',  // Base red
    600: '#b91c1c',  // DEFAULT - Primary error
    700: '#991b1b',  // Darker red
    800: '#7a1616',  // Very dark red
    900: '#5a1010',  // Darkest red
  },

  // Info - Industrial Steel Blue (Information)
  steel: {
    50: '#f1f5f9',
    100: '#e2e8f0',
    200: '#cbd5e1',
    300: '#94a3b8',
    400: '#64748b',
    500: '#475569',  // DEFAULT - Primary info
    600: '#334155',  // Darker steel
    700: '#1e293b',  // Very dark steel
    800: '#0f172a',  // Darkest steel
    900: '#020617',  // Almost black steel
  },

  // Neutral Grays - Industrial Scale
  gray: {
    0: '#FFFFFF',    // Pure white
    50: '#F8FAFC',   // Almost white
    100: '#F1F5F9',  // Very light gray
    200: '#E2E8F0',  // Light gray
    300: '#CBD5E1',  // Medium-light gray
    400: '#94A3B8',  // Medium gray
    500: '#64748B',  // Base gray
    600: '#475569',  // Medium-dark gray
    700: '#334155',  // Dark gray
    800: '#1E293B',  // Very dark gray
    900: '#0F172A',  // Almost black
    950: '#020617',  // Near black
    1000: '#000000', // Pure black
  },
};

// Light Mode Color Assignments (WCAG AA Compliant)
const LIGHT_MODE_COLORS = {
  // Primary Colors
  primary: INDUSTRIAL_PALETTE.teal[600],        // #204448
  primaryHover: INDUSTRIAL_PALETTE.teal[500],   // Lighter on hover
  primaryActive: INDUSTRIAL_PALETTE.teal[700],  // Darker on click
  primaryBg: INDUSTRIAL_PALETTE.teal[50],       // Background tint
  primaryBorder: INDUSTRIAL_PALETTE.teal[300],  // Border color

  // Success Colors
  success: INDUSTRIAL_PALETTE.green[600],       // #2d7a3e
  successHover: INDUSTRIAL_PALETTE.green[500],
  successActive: INDUSTRIAL_PALETTE.green[700],
  successBg: INDUSTRIAL_PALETTE.green[50],
  successBorder: INDUSTRIAL_PALETTE.green[300],

  // Warning Colors
  warning: INDUSTRIAL_PALETTE.amber[600],       // #d97706
  warningHover: INDUSTRIAL_PALETTE.amber[500],
  warningActive: INDUSTRIAL_PALETTE.amber[700],
  warningBg: INDUSTRIAL_PALETTE.amber[50],
  warningBorder: INDUSTRIAL_PALETTE.amber[300],

  // Error Colors
  error: INDUSTRIAL_PALETTE.red[600],           // #b91c1c
  errorHover: INDUSTRIAL_PALETTE.red[500],
  errorActive: INDUSTRIAL_PALETTE.red[700],
  errorBg: INDUSTRIAL_PALETTE.red[50],
  errorBorder: INDUSTRIAL_PALETTE.red[300],

  // Info Colors
  info: INDUSTRIAL_PALETTE.steel[500],          // #475569
  infoHover: INDUSTRIAL_PALETTE.steel[400],
  infoActive: INDUSTRIAL_PALETTE.steel[600],
  infoBg: INDUSTRIAL_PALETTE.steel[50],
  infoBorder: INDUSTRIAL_PALETTE.steel[300],

  // Background Colors
  bgBase: INDUSTRIAL_PALETTE.gray[0],           // White
  bgContainer: INDUSTRIAL_PALETTE.gray[0],      // White
  bgElevated: INDUSTRIAL_PALETTE.gray[0],       // White (cards)
  bgLayout: INDUSTRIAL_PALETTE.gray[50],        // Very light gray
  bgSpotlight: INDUSTRIAL_PALETTE.gray[100],    // Light gray

  // Border Colors
  border: INDUSTRIAL_PALETTE.gray[200],         // Light border
  borderSecondary: INDUSTRIAL_PALETTE.gray[100], // Lighter border

  // Text Colors (WCAG AA - 4.5:1 contrast minimum)
  textPrimary: INDUSTRIAL_PALETTE.gray[900],    // Almost black
  textSecondary: INDUSTRIAL_PALETTE.gray[600],  // Medium-dark gray
  textTertiary: INDUSTRIAL_PALETTE.gray[500],   // Medium gray
  textDisabled: INDUSTRIAL_PALETTE.gray[400],   // Light gray
};

// Dark Mode Color Assignments (WCAG AA Compliant)
const DARK_MODE_COLORS = {
  // Primary Colors (Brighter for dark backgrounds)
  primary: INDUSTRIAL_PALETTE.teal[500],        // Lighter teal for visibility
  primaryHover: INDUSTRIAL_PALETTE.teal[400],   // Even lighter on hover
  primaryActive: INDUSTRIAL_PALETTE.teal[600],  // Slightly darker on click
  primaryBg: INDUSTRIAL_PALETTE.teal[900],      // Very dark teal bg
  primaryBorder: INDUSTRIAL_PALETTE.teal[700],  // Dark teal border

  // Success Colors
  success: INDUSTRIAL_PALETTE.green[500],       // Lighter green
  successHover: INDUSTRIAL_PALETTE.green[400],
  successActive: INDUSTRIAL_PALETTE.green[600],
  successBg: INDUSTRIAL_PALETTE.green[900],
  successBorder: INDUSTRIAL_PALETTE.green[700],

  // Warning Colors
  warning: INDUSTRIAL_PALETTE.amber[500],       // Lighter amber
  warningHover: INDUSTRIAL_PALETTE.amber[400],
  warningActive: INDUSTRIAL_PALETTE.amber[600],
  warningBg: INDUSTRIAL_PALETTE.amber[900],
  warningBorder: INDUSTRIAL_PALETTE.amber[700],

  // Error Colors
  error: INDUSTRIAL_PALETTE.red[500],           // Lighter red
  errorHover: INDUSTRIAL_PALETTE.red[400],
  errorActive: INDUSTRIAL_PALETTE.red[600],
  errorBg: INDUSTRIAL_PALETTE.red[900],
  errorBorder: INDUSTRIAL_PALETTE.red[700],

  // Info Colors
  info: INDUSTRIAL_PALETTE.steel[400],          // Lighter steel blue
  infoHover: INDUSTRIAL_PALETTE.steel[300],
  infoActive: INDUSTRIAL_PALETTE.steel[500],
  infoBg: INDUSTRIAL_PALETTE.steel[900],
  infoBorder: INDUSTRIAL_PALETTE.steel[700],

  // Background Colors (Progressive depth)
  bgBase: INDUSTRIAL_PALETTE.gray[900],         // Very dark
  bgContainer: INDUSTRIAL_PALETTE.gray[800],    // Dark containers
  bgElevated: INDUSTRIAL_PALETTE.gray[700],     // Elevated cards
  bgLayout: INDUSTRIAL_PALETTE.gray[950],       // Almost black layout
  bgSpotlight: INDUSTRIAL_PALETTE.gray[800],    // Highlighted areas

  // Border Colors
  border: INDUSTRIAL_PALETTE.gray[700],         // Medium-dark border
  borderSecondary: INDUSTRIAL_PALETTE.gray[800], // Darker border

  // Text Colors (WCAG AA compliant on dark backgrounds)
  textPrimary: INDUSTRIAL_PALETTE.gray[50],     // Almost white
  textSecondary: INDUSTRIAL_PALETTE.gray[300],  // Medium-light gray
  textTertiary: INDUSTRIAL_PALETTE.gray[400],   // Medium gray
  textDisabled: INDUSTRIAL_PALETTE.gray[500],   // Darker gray
};

// Ant Design Theme Generator
const FONT_SCALE_MAP = {
  sm: 0.85,
  md: 1,
  lg: 1.15,
  xl: 1.3,
};

export const getAntdTheme = (isDark = false, fontScale = 'md') => {
  const colors = isDark ? DARK_MODE_COLORS : LIGHT_MODE_COLORS;
  const s = FONT_SCALE_MAP[fontScale] || 1;

  return {
    token: {
      // Primary Colors
      colorPrimary: colors.primary,
      colorPrimaryHover: colors.primaryHover,
      colorPrimaryActive: colors.primaryActive,
      colorPrimaryBg: colors.primaryBg,
      colorPrimaryBorder: colors.primaryBorder,

      // Success Colors
      colorSuccess: colors.success,
      colorSuccessHover: colors.successHover,
      colorSuccessActive: colors.successActive,
      colorSuccessBg: colors.successBg,
      colorSuccessBorder: colors.successBorder,

      // Warning Colors
      colorWarning: colors.warning,
      colorWarningHover: colors.warningHover,
      colorWarningActive: colors.warningActive,
      colorWarningBg: colors.warningBg,
      colorWarningBorder: colors.warningBorder,

      // Error Colors
      colorError: colors.error,
      colorErrorHover: colors.errorHover,
      colorErrorActive: colors.errorActive,
      colorErrorBg: colors.errorBg,
      colorErrorBorder: colors.errorBorder,

      // Info Colors
      colorInfo: colors.info,
      colorInfoHover: colors.infoHover,
      colorInfoActive: colors.infoActive,
      colorInfoBg: colors.infoBg,
      colorInfoBorder: colors.infoBorder,

      // Background Colors
      colorBgBase: colors.bgBase,
      colorBgContainer: colors.bgContainer,
      colorBgElevated: colors.bgElevated,
      colorBgLayout: colors.bgLayout,
      colorBgSpotlight: colors.bgSpotlight,

      // Border Colors
      colorBorder: colors.border,
      colorBorderSecondary: colors.borderSecondary,

      // Text Colors
      colorText: colors.textPrimary,
      colorTextSecondary: colors.textSecondary,
      colorTextTertiary: colors.textTertiary,
      colorTextQuaternary: colors.textDisabled,

      // Typography — scaled by fontScale (sm/md/lg/xl)
      fontFamily: '"Inter", "Roboto", "Helvetica Neue", "Arial", sans-serif',
      fontSize: Math.round(14 * s),
      fontSizeSM: Math.round(12 * s),
      fontSizeLG: Math.round(16 * s),
      fontSizeXL: Math.round(20 * s),
      fontSizeHeading1: Math.round(38 * s),
      fontSizeHeading2: Math.round(30 * s),
      fontSizeHeading3: Math.round(24 * s),
      fontSizeHeading4: Math.round(20 * s),
      fontSizeHeading5: Math.round(16 * s),

      // Layout
      borderRadius: 6,
      borderRadiusLG: 8,
      borderRadiusSM: 4,
      borderRadiusXS: 2,

      // Spacing
      padding: 16,
      paddingLG: 24,
      paddingSM: 12,
      paddingXS: 8,
      paddingXXS: 4,

      // Motion - Industry standard timing
      motionDurationFast: '0.1s',
      motionDurationMid: '0.2s',
      motionDurationSlow: '0.3s',

      // Shadows (subtle, professional)
      boxShadow: isDark
        ? '0 1px 2px 0 rgba(0, 0, 0, 0.5), 0 1px 6px -1px rgba(0, 0, 0, 0.6)'
        : '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02)',
      boxShadowSecondary: isDark
        ? '0 6px 16px 0 rgba(0, 0, 0, 0.7), 0 3px 6px -4px rgba(0, 0, 0, 0.8)'
        : '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12)',

      // Line Height (readability optimized)
      lineHeight: 1.5715,
      lineHeightHeading1: 1.2105,
      lineHeightHeading2: 1.2666,
      lineHeightHeading3: 1.3333,
      lineHeightHeading4: 1.4,
      lineHeightHeading5: 1.5,
    },

    components: {
      // Button Component
      Button: {
        controlHeight: 36,
        controlHeightLG: 44,
        controlHeightSM: 28,
        fontWeight: 500,
        borderRadius: 6,
        primaryShadow: 'none',  // NO GRADIENTS - Solid only
        defaultShadow: 'none',
      },

      // Input Component
      Input: {
        controlHeight: 36,
        controlHeightLG: 44,
        controlHeightSM: 28,
        borderRadius: 6,
        colorBgContainer: colors.bgContainer,
      },

      // Select Component
      Select: {
        controlHeight: 36,
        controlHeightLG: 44,
        controlHeightSM: 28,
        borderRadius: 6,
        colorBgContainer: colors.bgContainer,
      },

      // Card Component
      Card: {
        borderRadiusLG: 8,
        paddingLG: 24,
        colorBgContainer: colors.bgContainer,
      },

      // Table Component
      Table: {
        headerBg: isDark ? colors.bgContainer : INDUSTRIAL_PALETTE.gray[50],
        headerColor: colors.textPrimary,
        borderColor: colors.border,
        colorBgContainer: colors.bgContainer,
      },

      // Layout Component
      Layout: {
        headerBg: colors.bgContainer,
        headerPadding: '0 24px',
        headerHeight: 64,
        siderBg: isDark ? INDUSTRIAL_PALETTE.gray[950] : INDUSTRIAL_PALETTE.gray[900],
        bodyBg: colors.bgLayout,
      },

      // Menu Component
      Menu: {
        darkItemBg: INDUSTRIAL_PALETTE.gray[950],
        darkItemSelectedBg: colors.primary,
        darkItemHoverBg: colors.bgContainer,
        itemBorderRadius: 6,
        itemSelectedColor: colors.primary,
        itemHoverColor: colors.primaryHover,
      },

      // Notification Component
      Notification: {
        width: 384,
        colorBgElevated: colors.bgElevated,
      },

      // Modal Component
      Modal: {
        borderRadiusLG: 8,
        colorBgElevated: colors.bgElevated,
      },

      // Tabs Component
      Tabs: {
        itemActiveColor: colors.primary,
        itemHoverColor: colors.primaryHover,
        itemSelectedColor: colors.primary,
      },

      // Progress Component
      Progress: {
        defaultColor: colors.primary,
        remainingColor: colors.border,
      },

      // Tooltip Component - Dark background with white text
      Tooltip: {
        colorBgSpotlight: isDark ? INDUSTRIAL_PALETTE.gray[700] : INDUSTRIAL_PALETTE.gray[800],
        colorTextLightSolid: INDUSTRIAL_PALETTE.gray[0],
        borderRadius: 6,
        fontSize: 13,
      },
    },

    // Apply dark algorithm if dark mode
    ...(isDark && { algorithm: theme.darkAlgorithm }),
  };
};

// Export color palette for custom components
export const defaultColors = {
  // Primary Colors (use these in your components)
  primary: INDUSTRIAL_PALETTE.teal[600],        // #204448
  primaryLight: INDUSTRIAL_PALETTE.teal[500],   // For dark mode
  primaryDark: INDUSTRIAL_PALETTE.teal[700],    // For active states

  // Semantic Colors
  success: INDUSTRIAL_PALETTE.green[600],       // #2d7a3e
  successLight: INDUSTRIAL_PALETTE.green[500],
  warning: INDUSTRIAL_PALETTE.amber[600],       // #d97706
  warningLight: INDUSTRIAL_PALETTE.amber[500],
  error: INDUSTRIAL_PALETTE.red[600],           // #b91c1c
  errorLight: INDUSTRIAL_PALETTE.red[500],
  info: INDUSTRIAL_PALETTE.steel[500],          // #475569
  infoLight: INDUSTRIAL_PALETTE.steel[400],

  // Neutral Grays (array for backward compatibility)
  neutral: [
    INDUSTRIAL_PALETTE.gray[50],
    INDUSTRIAL_PALETTE.gray[100],
    INDUSTRIAL_PALETTE.gray[200],
    INDUSTRIAL_PALETTE.gray[300],
    INDUSTRIAL_PALETTE.gray[400],
    INDUSTRIAL_PALETTE.gray[500],
    INDUSTRIAL_PALETTE.gray[600],
    INDUSTRIAL_PALETTE.gray[700],
    INDUSTRIAL_PALETTE.gray[800],
    INDUSTRIAL_PALETTE.gray[900],
  ],

  // Grey palette (alias for gray)
  grey: INDUSTRIAL_PALETTE.gray,
  gray: INDUSTRIAL_PALETTE.gray,

  // Text colors
  text: {
    primary: INDUSTRIAL_PALETTE.gray[900],
    secondary: INDUSTRIAL_PALETTE.gray[700],
    disabled: INDUSTRIAL_PALETTE.gray[500],
  },

  // Full palette access
  palette: INDUSTRIAL_PALETTE,
};

// Export utility function to get color based on mode
export const getColor = (colorName, isDark = false) => {
  const colors = isDark ? DARK_MODE_COLORS : LIGHT_MODE_COLORS;
  return colors[colorName] || colors.primary;
};

/**
 * Get sidebar/theme color based on dark/light mode
 * In dark mode, returns a darker variant of the theme color
 * @param {string} themeColor - The base theme color (from Redux or default #204448)
 * @param {boolean} isDark - Whether dark mode is active
 * @returns {string} - The appropriate color for the current mode
 */
export const getSidebarColor = (themeColor, isDark = false) => {
  const defaultColor = INDUSTRIAL_PALETTE.teal[600]; // #204448
  const baseColor = themeColor || defaultColor;

  if (!isDark) {
    return baseColor;
  }

  // In dark mode, return darker variant
  // If it's the default teal color, use the dark mode teal
  if (baseColor === defaultColor || baseColor === '#204448') {
    return '#0F172A'; // Dark mode teal color
  }

  // For custom colors, darken by reducing brightness
  // This creates a darker variant of any custom theme color
  return darkenColor(baseColor, 0.4); // 40% darker
};

/**
 * Get button/component accent color based on dark/light mode
 * @param {string} themeColor - The base theme color
 * @param {boolean} isDark - Whether dark mode is active
 * @returns {string} - The appropriate accent color
 */
export const getAccentColor = (themeColor, isDark = false) => {
  const defaultColor = INDUSTRIAL_PALETTE.teal[600];
  const baseColor = themeColor || defaultColor;

  if (!isDark) {
    return baseColor;
  }

  // In dark mode, use the dark mode color for consistency
  if (baseColor === defaultColor || baseColor === '#204448') {
    return '#0F172A'; // Dark mode teal color
  }

  return darkenColor(baseColor, 0.25); // 25% darker for buttons
};

/**
 * Utility function to darken a hex color
 * @param {string} hex - Hex color code
 * @param {number} percent - Amount to darken (0-1)
 * @returns {string} - Darkened hex color
 */
const darkenColor = (hex, percent) => {
  // Remove # if present
  hex = hex.replace('#', '');

  // Parse hex to RGB
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Darken
  r = Math.floor(r * (1 - percent));
  g = Math.floor(g * (1 - percent));
  b = Math.floor(b * (1 - percent));

  // Convert back to hex
  return '#' +
    r.toString(16).padStart(2, '0') +
    g.toString(16).padStart(2, '0') +
    b.toString(16).padStart(2, '0');
};

// Export dark mode sidebar colors for direct use
export const darkModeSidebarColor = '#0F172A'; // Dark mode teal color
export const darkModeAccentColor = '#0F172A';  // Dark mode accent color
